/**
 * A dormer on its face as the 2D view draws it, and fitting a new one onto its face (phase 6). Pure (tested in
 * dormers.test.ts); the geometry is dormers.ts's, the same as the server's.
 */
import type { Vec2 } from "../editor/geo.ts";
import type { LiveRoof } from "../editor/roof.ts";
import { dormerGeometry, type SkDormer } from "./dormers.ts";

function inside([x, y]: Vec2, ring: Vec2[]): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** A dormer as the 2D view draws it: its faces in plan, its ridge and its width × depth box. */
export function dormerShape(roof: LiveRoof, d: SkDormer): { id: string; box: Vec2[]; faces: { key: string; plan: Vec2[] }[]; ridge: [Vec2, Vec2] | null; valid: boolean } {
  const host = roof.faces.find((f) => f.id === d.faceId);
  if (!host) return { id: d.id, box: [], faces: [], ridge: null, valid: false };
  const g = dormerGeometry({ normal: host.normal, d: host.d, azimuth: host.azimuth }, d);
  if (!g) {
    const fallback = dormerGeometry({ normal: host.normal, d: host.d, azimuth: host.azimuth }, { ...d, height: 0.3, pitch: 5 });
    return { id: d.id, box: fallback?.box ?? [], faces: [], ridge: null, valid: false };
  }
  const faces = g.faces.map((f) => ({ key: f.key, plan: f.points.map((q) => [q[0], q[1]] as Vec2) }));
  const left = g.faces.find((f) => f.key === "left");
  const right = g.faces.find((f) => f.key === "right");
  let ridge: [Vec2, Vec2] | null = null;
  if (left && right) {
    const shared = left.points.filter((q) => right.points.some((r) => Math.hypot(r[0] - q[0], r[1] - q[1]) < 1e-6 && Math.abs(r[2] - q[2]) < 1e-6));
    if (shared.length >= 2) ridge = [[shared[0]![0], shared[0]![1]], [shared[1]![0], shared[1]![1]]];
  }
  // inside its face's outline (its own cut is the face's hole in the live model, so holes are not checked)
  const valid = g.box.every((q) => inside(q, host.plan));
  return { id: d.id, box: g.box, faces, ridge, valid };
}

/** A new dormer where the installer clicked, else the nearest place (within 4 m) where it stands inside its face (a
 * click near an edge or the ridge made a dormer the server refuses, which held up the live numbers and the save);
 * null when it fits nowhere near. */
export function fitDormer(roof: LiveRoof, d: SkDormer): SkDormer | null {
  if (dormerShape(roof, d).valid) return d;
  for (let r = 0.25; r <= 4 + 1e-9; r += 0.25)
    for (let k = 0; k < 16; k++) {
      const a = (k * Math.PI) / 8;
      const tried: SkDormer = { ...d, center: [d.center[0] + r * Math.cos(a), d.center[1] + r * Math.sin(a)] };
      if (dormerShape(roof, tried).valid) return tried;
    }
  return null;
}
